import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import '@fontsource/inter';
import theme from './theme/muiTheme';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ActingPartnerProvider } from './contexts/ActingPartnerContext';
import { CountriesProvider } from './contexts/CountriesContext';
import { useAuth } from './hooks/useAuth';
import AppLayout from './components/layout/AppLayout';
import protectedRoutes from './config/routes';
import LoginPage from './pages/auth/LoginPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
// Eager on purpose: it has to render while offline, when no chunk can load.
import NoNetworkGuard from 'components/noNetwork/NoNetworkGuard';
import PortalSpinner from 'components/loading/PortalSpinner';
import { size } from 'theme/tokens';
import { ADMIN_PERMISSIONS, assertNavPermissionCoverage, canSeePath } from 'constants/navPermissions';
import { RBAC_NAV_ENABLED, firstVisiblePath } from 'helpers/navVisibility';
import navigationConfig from './config/navigation';
import { useListFilterReset } from 'helpers/listFilterMemory';

/**
 * Warn, in development, about admin routes nobody mapped to a permission.
 *
 * Module scope so it runs once per load rather than once per render. The gate
 * fails OPEN on an unmapped path — the right runtime behaviour, and a terrible
 * way to discover you forgot one, since the screen just keeps working for
 * everybody and looks gated.
 *
 * The route table stores child paths relative to the `/admin` parent (and the
 * dashboard as an index route with no path at all), while the permission table
 * is keyed absolutely, so the two are reconciled here rather than by storing
 * half-paths in the permission table where they would read as typos.
 */
assertNavPermissionCoverage(
  protectedRoutes
    .filter((route) => route.path !== '*')
    .map((route) => (route.index ? '/admin' : `/admin/${route.path}`)),
  ADMIN_PERMISSIONS,
  'platform admin',
);

function LoadingFallback() {
  return (
    <Box className="flex items-center justify-center" sx={{ minHeight: '100vh' }}>
      <PortalSpinner />
    </Box>
  );
}

/**
 * Signed in: download every page's chunk in the background, once. Without it
 * the first visit to each page showed the route fallback over the whole
 * content area while its code arrived, before the page could paint its own
 * header, filters and table loader.
 */
function PrefetchPages() {
  React.useEffect(() => {
    const run = () => protectedRoutes.forEach((route) => route.element.preload?.());
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    idle(run);
  }, []);
  return null;
}

/**
 * A page's chunk loading INSIDE the console: the ring sits in the content area,
 * the same 400px box the partner portal's AppLayout uses, rather than a
 * screen-tall one under the header.
 */
function PageFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: size[400] }}>
      <PortalSpinner size={size[36]} />
    </Box>
  );
}

/**
 * Signed in, and past the forced password rotation.
 *
 * The `mustChangePassword` redirect is what makes the rotation mandatory rather
 * than a suggestion: sign-in routes a first-login user to the change-password
 * screen, but nothing stopped them typing /admin and entering the console on the
 * temporary password from their invite email — the very credential the rotation
 * exists to retire.
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, mustChangePassword } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  if (mustChangePassword) {
    return <Navigate to="/admin/change-password" replace />;
  }
  return children;
}

/** Authenticated, but deliberately NOT behind the rotation gate above. */
function RotationRoute({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

/**
 * Signed in, and granted the module this screen belongs to.
 *
 * Wraps every entry in the route table rather than being opted into per route,
 * because the failure mode of opting in is a page somebody forgot — and a page
 * somebody forgot is an open door, not a visible bug. Applied here, the
 * permission table in constants/navPermissions.js is the only place that decides,
 * and the sidebar filter reads the SAME table, so "hidden from the nav" and
 * "refuses a typed URL" cannot disagree.
 *
 * ⚠️ Affordance, not enforcement. `permissions` is client-side data from the
 * login response and whoever is sitting at the browser can edit it. This stops
 * an operator wandering into a screen their role does not cover; it does not stop
 * an attacker. Every protected route is enforced independently in app-backend
 * (ENABLE_ROUTE_AUTHZ), and that is the boundary.
 *
 * Renders the 403 in place instead of redirecting: a redirect loses the URL, so
 * the operator cannot paste it to the administrator they are about to ask for
 * access, and a bounce to the dashboard is indistinguishable from the link being
 * broken.
 */
function PermissionRoute({ children }) {
  const { perms } = useAuth();
  const { pathname } = useLocation();

  if (!RBAC_NAV_ENABLED) return children;
  if (canSeePath(perms, pathname)) return children;

  /**
   * The Dashboard is itself a gated module, and it is where sign-in, the
   * catch-all and every "back to safety" button send people. A role without
   * `admin:Dashboard` would otherwise be dropped on a 403 as its FIRST screen
   * after signing in, with a button that returns it to the same 403.
   *
   * Handling it here rather than at each redirect means every route into the
   * dashboard is covered by one rule — including bookmarks, which no redirect
   * site can see. Only the portal root gets this treatment: a denied deep link
   * must still show the 403, because silently landing somewhere else would hide
   * the reason the link did not work.
   */
  if (pathname === '/admin' || pathname === '/admin/') {
    const fallback = firstVisiblePath(navigationConfig, perms);
    if (fallback) return <Navigate to={fallback} replace />;
  }
  return <AccessDeniedPage />;
}

function AdminRoutes() {
  // List filters survive a detail-page round trip; a sidebar menu clears them.
  useListFilterReset(navigationConfig);
  return (
    <Routes>
      <Route path="login" element={<LoginPage />} />
      <Route
        path="change-password"
        element={
          <RotationRoute>
            <ChangePasswordPage />
          </RotationRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {/* Inside ProtectedRoute so it reads a session that exists, and
                OUTSIDE the route table so the partner an admin is booking for
                survives moving between Start Chat and Shop Travel. */}
            <ActingPartnerProvider>
              {/* Same placement, same reasons: the country list is fetched
                  once per login (it needs the session's token) and held
                  across every route. */}
              <CountriesProvider>
                <SidebarProvider>
                  <PrefetchPages />
                  <AppLayout />
                </SidebarProvider>
              </CountriesProvider>
            </ActingPartnerProvider>
          </ProtectedRoute>
        }
      >
        {protectedRoutes.map((route) => {
          const Element = route.element;
          // PermissionRoute sits OUTSIDE Suspense so a denied screen answers
          // immediately instead of downloading the chunk for a page it is about
          // to refuse. NotFound (`*`) is left ungated — a 403 for a URL that
          // does not exist tells the operator the wrong thing.
          const element =
            route.path === '*' ? (
              <Suspense fallback={<PageFallback />}>
                <Element />
              </Suspense>
            ) : (
              <PermissionRoute>
                <Suspense fallback={<PageFallback />}>
                  <Element />
                </Suspense>
              </PermissionRoute>
            );

          if (route.index) {
            return <Route key="index" index element={element} />;
          }
          return <Route key={route.path} path={route.path} element={element} />;
        })}
      </Route>
    </Routes>
  );
}

export default function AdminApp() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <AdminRoutes />
        </Suspense>
      </AuthProvider>
      {/* Inside the theme (CRM purple), outside the auth gate (covers login
          too), and beside the routes rather than around them: it overlays the
          console, so nothing underneath — MFA state, keep-alive, typed forms —
          is unmounted by an outage. */}
      <NoNetworkGuard />
    </ThemeProvider>
  );
}
